/**
 * @fileoverview Hook Actions Layer optimizado para CustomerDealItemMC. Maneja eliminación de negociación (deal) y loading state con separación estricta de responsabilidades y performance avanzada.
 * @module useCustomerDealItemActions
 * @category Hook - Actions Layer
 * @since 1.0.0
 * @version 1.1.0 - JSDoc completo, performance y ejemplos
 *
 * @description
 * Hook especializado de **Actions Layer** para CustomerDealItemMC. Implementa separación estricta de responsabilidades:
 * - ✅ Solo operaciones de negocio (eliminación de deal, loading, dispatch de error)
 * - ❌ Sin lógica de UI ni manipulación de datos
 * - ✅ Optimizaciones: useCallback para handlers, manejo robusto de errores, integración Redux
 *
 * **Flujo de operación:**
 * 1. Solicita confirmación al usuario para eliminar la negociación
 * 2. Ejecuta la eliminación vía API (dealsModelMC)
 * 3. Llama callback para recargar deals si aplica
 * 4. Maneja loading y errores vía Redux
 *
 * **Performance:**
 * - Handler principal memoizado (useCallback)
 * - Solo cambia loading en operaciones críticas
 * - Sin side effects fuera del scope del hook
 *
 * @example
 * // Ejemplo de uso en componente
 * import { useCustomerDealItemActions } from 'presentation/hooks/inbox/actions/useCustomerDealItemActions';
 *
 * const { loading, handleDeleteDeal } = useCustomerDealItemActions({ deal, reloadDeals });
 *
 * <ButtonMC loading={loading} onClick={handleDeleteDeal}>Eliminar</ButtonMC>
 *
 * @see CustomerDealItemMC Para integración visual
 */
import { useCallback, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useDispatch } from "react-redux";
import dealsModelMC from '../../../models/DealsModelMC';
import { LOAD_DATA_FAILURE } from '../../../../actions/inbox/conversationActions';

/**
 * @typedef {Object} CustomerDealItemActionsParams
 * @description Parámetros de entrada para el hook useCustomerDealItemActions
 * @property {Object} deal - Objeto de negociación (deal) con al menos web_id
 * @property {Function} reloadDeals - Callback opcional para recargar deals tras eliminar
 */

/**
 * Hook Actions Layer optimizado para CustomerDealItemMC.
 *
 * Implementa el patrón de **Actions Layer** con separación estricta de responsabilidades:
 * - Solo operaciones de negocio (eliminación de deal, loading, dispatch de error)
 * - Sin lógica de UI ni manipulación de datos
 *
 * @function useCustomerDealItemActions
 * @category Hook - Actions Layer
 *
 * @param {CustomerDealItemActionsParams} params - Parámetros de entrada para el hook
 * @returns {CustomerDealItemActionsResult} Objeto con loading y handleDeleteDeal
 *
 * @typedef {Object} CustomerDealItemActionsResult
 * @property {boolean} loading - Estado de loading durante la operación
 * @property {Function} handleDeleteDeal - Handler asíncrono para eliminar la negociación
 *
 * @example
 * // Uso típico en componente
 * const { loading, handleDeleteDeal } = useCustomerDealItemActions({ deal, reloadDeals });
 *
 * @see CustomerDealItemMC
 * @since 1.0.0
 * @version 1.1.0 - JSDoc completo, performance y ejemplos
 */
export function useCustomerDealItemActions({ deal, reloadDeals }) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('InboxMC');

  /**
   * Handler asíncrono para eliminar la negociación actual tras confirmación del usuario.
   *
   * @async
   * @function handleDeleteDeal
   * @memberof useCustomerDealItemActions
   * @description
   * **Flujo de operación:**
   * 1. Solicita confirmación al usuario (window.confirm)
   * 2. Si confirma, ejecuta la eliminación vía dealsModelMC.remove
   * 3. Llama reloadDeals si está definido
   * 4. Maneja loading y errores vía Redux
   *
   * **Validaciones:**
   * - Solo elimina si el usuario confirma
   * - Maneja errores y los despacha a Redux
   *
   * @throws {Error} Si la eliminación falla, despacha error a Redux
   * @returns {Promise<void>} No retorna valor, solo efectos secundarios
   *
   * @example
   * // Uso básico
   * await handleDeleteDeal();
   */
  const handleDeleteDeal = useCallback(async () => {
    if (window.confirm(t('deal.delete_confirm'))) {
      try {
        setLoading(true);
        await dealsModelMC.remove({ id: deal.web_id });
        reloadDeals?.();
      } catch (error) {
        const errorMessage = error?.message || error || t('deal.delete_error');
        dispatch({ type: LOAD_DATA_FAILURE, error: errorMessage });
      } finally {
        setLoading(false);
      }
    }
  }, [deal.web_id, dispatch, reloadDeals, t]);

  return { loading, handleDeleteDeal };
}
